import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { State } from './assignments.reducer';
import * as fromApp from '../../../store/app.reducer';
import * as AssignmentsActions from './assignments.actions';
import { environment } from '../../../../environments/environment';
import { catchError, debounceTime, exhaustMap, filter, finalize, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { PaginationModel } from '../../../models/pagination.model';
import { AssignmentModel, AssignmentMonthModel } from '../../../models/assignment.model';
import { AssignmentCategoryModel } from '../../../models/assignmentCategory.model';
import { GuardModel } from '../../../models/guard.model';
import { PriceModel } from '../../../models/price.model';
import { ShiftModel } from '../../../models/shift.model';
import { RateModel } from '../../../models/rate.model';
import { ReportModel } from '../../../models/report.model';
import { CustomerModel } from '../../../models/customer.model';
import { AssignmentStatisticsModel } from '../../../models/assignment.statistics.model';
import { AssignmentStatusConst } from '../../../constants/assignment.status.const';
import { ReportStatistic } from '../../../models/reportStatistic';
import { PreviousAssignmentResponse } from 'src/app/models/previous-assignment.model';
import { SnackbarService } from '../../../shared/snackbar/snackbar.service';
import { JobPlaceModel } from 'src/app/models/jobPlace.model';
import { LoadingService } from 'src/app/service/loading-service.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class AssignmentsEffects {

    constructor(
        private actions$: Actions,
        private http: HttpClient,
        private router: Router,
        private assignmentStatus: AssignmentStatusConst,
        private store: Store<fromApp.AppState>,
        private snackbarService: SnackbarService,
        private loadingService: LoadingService,
        private translate: TranslateService
    ) {
    }

    loadAssignments = createEffect(() => {
        return this.actions$.pipe(
            ofType(AssignmentsActions.LOAD_ASSIGNMENTS),
            switchMap((action: AssignmentsActions.LoadAssignments) => {
                let params = new HttpParams();
                params = params.append('page', action.payload.page);
                params = params.append('per_page', 10);
                params = params.append('sort', 'desc');
                params = params.append('only', 'id,startAt,endAt,jobPlace,numberOfPersonnel,totalCost,status,skill');
                params = params.append('status', [this.assignmentStatus.PUBLISH, this.assignmentStatus.ONGOING, this.assignmentStatus.FINISHED].toString());

                if (action.payload.params) {
                    action.payload.params.forEach(param => {
                        params = params.append(param.key, param.value);
                    });
                }

                return this.http.get(environment.apiUrl + `/customers/${action.payload.customerId}/assignments`, { params })
                    .pipe(
                        switchMap((resData: { data: AssignmentModel[], pagination: PaginationModel }) => {
                            return of(new AssignmentsActions.LoadAssignmentsSuccess({
                                assignments: resData.data,
                                pagination: resData.pagination,
                            }));
                        }),
                        catchError(error => {
                            return of(new AssignmentsActions.LoadAssignmentsFailed());
                        }),
                    );
            }),
        );
    });

    loadAssignmentsStatistic = createEffect(() => {
        return this.actions$.pipe(
            ofType(AssignmentsActions.LOAD_ASSIGNMENTS_STATISTICS),
            withLatestFrom(this.store.select(state => state.auth.customer)),
            switchMap(([action, customer]: [AssignmentsActions.LoadAssignmentsStatistics, CustomerModel]) => {
                return this.http.get<AssignmentStatisticsModel>(
                    environment.apiUrl + `/customers/${customer.id}/assignments/statistics`
                ).pipe(
                    switchMap((resData: AssignmentStatisticsModel) => {
                        return of(new AssignmentsActions.LoadAssignmentsStatisticsSuccess(resData));
                    }),
                    catchError(() => of(new AssignmentsActions.LoadAssignmentsFailed()))
                );
            }),
        );
    });

    loadJobOffers = createEffect(() => {
        return this.actions$.pipe(
            ofType<AssignmentsActions.LoadCustomerJobOffers>(
                AssignmentsActions.LOAD_CUSTOMER_JOB_OFFERS
            ),
            withLatestFrom(this.store.select(state => state.auth.customer)),
            filter(([, customer]) => !!customer),
            debounceTime(100),
            switchMap(([action, customer]) => {
                const { silent, ...apiPayload } = action.payload;
                if (!silent) {
                    this.loadingService.showLoader();
                }

                return this.http
                    .post<{ data: AssignmentModel[]; month: AssignmentMonthModel }>(
                        `${environment.apiUrl}/customers/${customer?.id}/get-job-offers`,
                        apiPayload
                    )
                    .pipe(
                        map(resData => new AssignmentsActions.LoadCustomerJobOffersSuccess(resData)),
                        catchError(() => of(new AssignmentsActions.LoadCustomerJobOffersFailed())),
                        finalize(() => {
                            if (!silent) {
                                this.loadingService.hideLoader();
                            }
                        })
                    );
            })
        );
    });

    // toggle api for defualt job
    makeDefualtJob = createEffect(() => {
        return this.actions$.pipe(
            ofType(AssignmentsActions.LOAD_CUSTOMER_JOB_DEFUALT),
            withLatestFrom(this.store.select(state => state.auth.customer)),
            switchMap(([action, customer]: [AssignmentsActions.LoadCustomerdefualtJobPlace, CustomerModel]) => {
                return this.http.post(`${environment.apiUrl}/customers/${customer?.id}/job-places/set-default`, action.payload)
                    .pipe(
                        switchMap((resData: {
                            data: JobPlaceModel[]
                        }) => {
                            return of(new AssignmentsActions.LoadCustomerdefualtJobPlaceSuccess(resData));
                        }),
                        catchError(() => {
                            return of(new AssignmentsActions.LoadCustomerdefualtJobPlaceFailed());
                        }),
                    );
            }),
        );
    });

    updateShift = createEffect(() => {
        return this.actions$.pipe(
            ofType(AssignmentsActions.UPDATE_SHIFT),
            switchMap((action: AssignmentsActions.UpdateShift) => {
                this.loadingService.showLoader();
                return this.http.post(environment.apiUrl + `/shifts/${action.payload.id}`, action.payload)
                    .pipe(
                        switchMap((response: {
                            data: ShiftModel
                        }) => {
                            this.snackbarService.showSuccess(this.translate.instant('guard-shift-details.common.shift-updated-succesfully'));
                            return of(new AssignmentsActions.UpdateShiftSuccess(response));
                        }),
                        catchError(() => {
                            this.snackbarService.showError(this.translate.instant('guard-shift-details.common.shift-update-failed'));
                            return of(new AssignmentsActions.UpdateShiftFailed());
                        }),
                        finalize(() => {
                            this.loadingService.hideLoader();
                        })
                    );
            }),
        );
    });

    shiftApproveGuardHours = createEffect(() => {
        return this.actions$.pipe(
            ofType(AssignmentsActions.SHIFT_APPROVE_GUARD_HOURS),
            switchMap((action: AssignmentsActions.ShiftApproveGuardHours) => {
                return this.http.post(environment.apiUrl + `/shifts/${action.payload.shiftId}/approve-hours`, {
                    status: action.payload.status,
                    guardId: action.payload.guardId,
                })
                    .pipe(
                        switchMap((resData: {
                            data: ShiftModel
                        }) => {
                            return of(new AssignmentsActions.ShiftApproveGuardHoursSuccess(resData.data));
                        }),
                        catchError(() => {
                            return of(new AssignmentsActions.ShiftApproveGuardHoursFailed());
                        }),
                    );
            }),
        );
    });

    loadReports = createEffect(() => {
        return this.actions$.pipe(
            ofType(AssignmentsActions.LOAD_REPORTS),
            switchMap((action: AssignmentsActions.LoadReports) => {
                let params = new HttpParams();
                params = params.append('page', action.payload.page);

                return this.http.post(environment.apiUrl + `/customers/${action.payload.customerId}/reports`, {
                    ...action.payload.params,
                    incident_time: {
                        toTime: action.payload.toTime,
                        fromTime: action.payload.fromTime,
                    }
                }, {
                    params: params,
                })
                    .pipe(
                        switchMap((resData: { data: ReportModel[], pagination: PaginationModel }) => {
                            return of(new AssignmentsActions.LoadReportsSuccess(resData));
                        }),
                        catchError(() => {
                            return of(new AssignmentsActions.LoadReportsFailed());
                        }),
                    );
            }),
        );
    });

    loadReportsStatistics = createEffect(() => {
        return this.actions$.pipe(
            ofType(AssignmentsActions.LOAD_REPORTS_STATISTICS),
            switchMap((action: AssignmentsActions.LoadReportsStatistics) => {
                return this.http.post(environment.apiUrl + `/customers/${action.payload.customerId}/reports-statistics`, action.payload)
                    .pipe(
                        switchMap((resData: { data: ReportStatistic }) => {
                            return of(new AssignmentsActions.LoadReportsStatisticsSuccess(resData));
                        }),
                        catchError(() => {
                            return of(new AssignmentsActions.LoadReportsStatisticsFailed());
                        }),
                    );
            }),
        );
    });

    loadReport = createEffect(() => {
        return this.actions$.pipe(
            ofType(AssignmentsActions.LOAD_REPORT),
            switchMap((action: AssignmentsActions.LoadReport) => {
                return this.http.get(environment.apiUrl + `/customers/${action.payload.customerId}/reports/${action.payload.reportId}`)
                    .pipe(
                        switchMap((resData: { data: ReportModel }) => {
                            return of(new AssignmentsActions.LoadReportSuccess(resData));
                        }),
                        catchError(() => {
                            return of(new AssignmentsActions.LoadReportFailed());
                        }),
                    );
            }),
        );
    });

    acceptGuardsForShift = createEffect(() => {
        return this.actions$.pipe(
            ofType(AssignmentsActions.ACCEPT_GUARDS_FOR_SHIFT),
            switchMap((action: AssignmentsActions.AcceptGuardsForShift) => {

                return this.http.post(environment.apiUrl + `/shifts/${action.payload.shiftId}/accept`, {
                    guardIds: [String(action.payload.guardIds)]
                })
                    .pipe(
                        switchMap((resData: { data: ShiftModel }) => {
                            this.snackbarService.showSuccess(this.translate.instant('guards-picked'));
                            return [
                                new AssignmentsActions.UpdateAssignmentShift(resData.data),
                            ];
                        }),
                        catchError((error) => {
                            if (error.status == 400) {
                                this.snackbarService.showError(error.error.message);
                            } else {
                                this.snackbarService.showError(this.translate.instant('something-went-wrong'));
                            }
                            return of(new AssignmentsActions.LoadAssignmentsFailed());
                        }),
                    );
            }),
        );
    });

    rateGuardForShift = createEffect(() => {
        return this.actions$.pipe(
            ofType(AssignmentsActions.RATE_GUARD_FOR_SHIFT),
            switchMap((action: AssignmentsActions.RateGuardForShift) => {

                return this.http.post(environment.apiUrl + `/shifts/${action.payload.shiftId}/guard-rates`, {
                    guardId: action.payload.guardId,
                    rate: action.payload.rate,
                })
                    .pipe(
                        switchMap((resData: { data: RateModel }) => {
                            this.snackbarService.showSuccess(this.translate.instant('assignment-rated-success'));
                            return [
                                new AssignmentsActions.RateGuardForShiftSuccess({
                                    rate: resData.data,
                                    guardId: action.payload.guardId,
                                    shiftId: action.payload.shiftId,
                                }),
                                new AssignmentsActions.SelectShiftOnOpenAssignment(action.payload.shiftId),
                            ];
                        }),
                        catchError(() => {
                            return of(new AssignmentsActions.RateGuardForShiftFailed());
                        }),
                    );
            }),
        );
    });

    loadAssignmentDetails = createEffect(() => {
        return this.actions$.pipe(
            ofType(AssignmentsActions.LOAD_ASSIGNMENT_DETAILS),
            switchMap((action: AssignmentsActions.LoadAssignmentDetails) => {

                return this.http.get(environment.apiUrl + `/customers/${action.payload.customerId}/assignments/${action.payload.assignmentsId}`)
                    .pipe(
                        switchMap((resData: { data: AssignmentModel }) => {
                            return of(new AssignmentsActions.LoadAssignmentDetailsSuccess({
                                assignment: resData.data,
                            }));
                        }),
                        catchError(error => {
                            if (error.status === 403) {
                                this.snackbarService.showError(error.error.error);
                                this.router.navigate(['/components/assignments']);
                            }
                            return of(new AssignmentsActions.LoadAssignmentDetailsFailed());
                        }),
                    );
            }),
        );
    });

    cancelAssignment = createEffect(() => {
        return this.actions$.pipe(
            ofType(AssignmentsActions.CANCEL_ASSIGNMENT),
            switchMap((action: AssignmentsActions.CancelAssignment) => {
                return this.http.get(environment.apiUrl + `/customers/${action.payload.customerId}/assignments/${action.payload.assignmentId}/cancel`)
                    .pipe(
                        switchMap((resData: { data: AssignmentModel }) => {
                            this.snackbarService.showSuccess(this.translate.instant('assignment-canceled-success'));
                            return of(new AssignmentsActions.CancelAssignmentSuccess({
                                assignment: resData.data,
                            }));
                        }),
                        catchError(() => {
                            this.snackbarService.showError(this.translate.instant('something-went-wrong'));
                            return of(new AssignmentsActions.CancelAssignmentFailed());
                        }),
                    );
            }),
        );
    });

    addAssignment = createEffect(() => {
        return this.actions$.pipe(
            ofType(AssignmentsActions.ADD_ASSIGNMENT),
            switchMap((action: AssignmentsActions.AddAssignment) => {
                this.loadingService.showLoader();
                const data = action.payload.data || {};
                const payload = {
                    ...data,
                    assignment_category_id: data.assignment_category_id ?? data.assignmentCategoryId,
                    number_of_leaders: data.number_of_leaders ?? data.numberOfLeaders ?? 0,
                    dates: Array.isArray(data.dates) ? data.dates : (data.shifts ?? []),
                    job_place_id: data.job_place_id ?? data.jobPlaceId,
                    skill_id: data.skill_id ?? data.skillId,
                    contact_person_name: data.contact_person_name ?? data.contactPersonName ?? '',
                    contact_person_phone: data.contact_person_phone ?? data.contactPersonPhone ?? '',
                    description: data.description ?? null,
                    engagement_type: data.engagement_type ?? data.engagementType ?? 2,
                    number_of_personnel: data.number_of_personnel ?? data.numberOfPersonnel ?? 1,
                    guards_ids: data.guards_ids ?? data.guardsIds ?? [],
                    leaders_ids: data.leaders_ids ?? data.leadersIds ?? [],
                    timezone: data.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
                    uniform_id: data.uniform_id ?? data.uniformId
                };
                return this.http.post(environment.apiUrl + `/customers/${action.payload.customerId}/assignments`, payload)
                    .pipe(
                        switchMap((resData: any) => {
                            if (action.payload.data.status) {
                                this.snackbarService.showSuccess(this.translate.instant('saved-as-draft-success'));
                            }

                            // Handle response structure: { assignmentId: number, message: string } or { data: AssignmentModel }
                            const assignmentData = resData.data || resData;
                            const assignmentId = resData.assignmentId || assignmentData?.assignmentId || assignmentData?.id;

                            console.log('Assignment created. ID:', assignmentId);

                            return of(new AssignmentsActions.AddAssignmentSuccess({
                                assignment: {
                                    ...assignmentData,
                                    assignmentId: assignmentId
                                } as AssignmentModel,
                            }));
                        }),
                        catchError(() => {
                            if (action.payload.data.status) {
                                this.snackbarService.showError(this.translate.instant('failed-save-draft'));
                            }
                            else {
                                this.snackbarService.showError(this.translate.instant('failed-create-assignment'));
                            }
                            return of(new AssignmentsActions.AddAssignmentFailed());
                        }),
                        finalize(() => {
                            this.loadingService.hideLoader();
                        })
                    );
            }),
        );
    });

    addAssignmentAsDraft = createEffect(() => {
        return this.actions$.pipe(
            ofType(AssignmentsActions.ADD_ASSIGNMENT_AS_DRAFT),
            switchMap((action: AssignmentsActions.AddAssignmentAsDraft) => {
                const data = action.payload.data || {};
                const payload = {
                    ...data,
                    assignment_category_id: data.assignment_category_id ?? data.assignmentCategoryId,
                    number_of_leaders: data.number_of_leaders ?? data.numberOfLeaders ?? 0,
                    dates: data.dates ?? data.shifts ?? []
                };
                return this.http.post(environment.apiUrl + `/customers/${action.payload.customerId}/assignments-as-draft`, payload)
                    .pipe(
                        switchMap((resData: { data: AssignmentModel }) => {
                            if (action.payload.data.status) {
                                this.snackbarService.showSuccess(this.translate.instant('saved-as-draft-success'));
                            }
                            this.router.navigate(['/components/assignments/create-assignment/step1']);
                            return of(new AssignmentsActions.AddAssignmentAsDraftSuccess({
                                assignment: resData.data,
                            }));
                        }),
                        catchError(() => {
                            if (action.payload.data.status) {
                                this.snackbarService.showError(this.translate.instant('failed-save-draft'));
                            }
                            else {
                                this.snackbarService.showError(this.translate.instant('failed-create-assignment'));
                            }
                            return of(new AssignmentsActions.AddAssignmentAsDraftFailed());
                        }),
                    );
            }),
        );
    });

    calculatePrice = createEffect(() => {
        return this.actions$.pipe(
            ofType(AssignmentsActions.CALCULATE_PRICE),
            switchMap((action: AssignmentsActions.CalculatePrice) =>
                this.http.post<PriceModel>(
                    environment.apiUrl + `/customers/${action.payload.customerId}/assignments/calculate-price`,
                    {
                        skillId: action.payload.skillId,
                        guardsNumber: action.payload.guardsNumber,
                        dates: action.payload.dates,
                        timezone: action.payload.timezone,
                        jobPlaceId: action.payload.jobPlaceId,
                    },
                ).pipe(
                    map((resData) => new AssignmentsActions.CalculatePriceSuccess({ price: resData })),
                    catchError(() => of(new AssignmentsActions.CalculatePriceFailed())),
                ),
            ),
        );
    });

    loadCategories = createEffect(() => {
        return this.actions$.pipe(
            ofType(AssignmentsActions.LOAD_ASSIGNMENT_CATEGORIES),
            switchMap((action: AssignmentsActions.LoadAssignmentCategories) => {
                this.loadingService.showLoader();
                return this.http.get(environment.apiUrl + `/assignment-categories?sort=asc`)
                    .pipe(
                        switchMap((resData: { data: AssignmentCategoryModel[] }) => {
                            return of(new AssignmentsActions.LoadAssignmentCategoriesSuccess({
                                categories: resData.data,
                            }));
                        }),
                        catchError(() => {
                            return of(new AssignmentsActions.LoadAssignmentCategoriesFailed());
                        }),
                        finalize(() => {
                            this.loadingService.hideLoader();
                        })
                    );
            }),
        );
    });

    loadGuards = createEffect(() => {
        return this.actions$.pipe(
            ofType(AssignmentsActions.LOAD_OBJECT_GUARDS),
            withLatestFrom(this.store.select(state => state.assignments)),
            exhaustMap(([action, State]: [AssignmentsActions.LoadObjectGuards, State]) => {
                let params = new HttpParams()
                    .set('page', State.guardsMeta.page)
                    .set('per_page', State.guardsMeta.perPage);

                return this.http.post(
                    `${environment.apiUrl}/job-places/${action.payload.jobPlaceId}/guards-available-for-assignment`,
                    {
                        skillId: action.payload.skillId,
                        jobPlaceId: action.payload.jobPlaceId,
                        uniformId: action.payload.uniformId,
                        dates: action.payload.dates,
                        timezone: action.payload.timezone,
                        filterType: action.payload.filterType,
                        search: action.payload.search,
                    },
                    { params }
                ).pipe(
                    map((resData: { data: GuardModel[], pagination: PaginationModel }) =>
                        new AssignmentsActions.LoadObjectGuardsSuccess({
                            guards: resData.data,
                            pagination: resData.pagination,
                        })
                    ),
                    catchError(() => of(new AssignmentsActions.LoadObjectGuardsFailed()))
                );
            })
        );
    });


    loadPreviousAssignments = createEffect(() =>
        this.actions$.pipe(
            ofType(AssignmentsActions.LOAD_PREVIOUS_ASSIGNMENT),
            withLatestFrom(this.store.select(state => state.auth.customer)),
            exhaustMap(([action, customer]: [AssignmentsActions.LoadPreviousAssignment, CustomerModel]) => {
                const body: any = {};

                if (action.payload.assignment_category_id) body.assignment_category_id = action.payload.assignment_category_id;
                if (action.payload.skill_id) body.skill_id = action.payload.skill_id;
                if (action.payload.job_place_id) body.job_place_id = action.payload.job_place_id;
                if (action.payload.uniform_id) body.uniform_id = action.payload.uniform_id;

                const url = `${environment.apiUrl}/customers/${customer?.id}/assignments/previous`;

                return this.http.post<PreviousAssignmentResponse>(url, body).pipe(
                    map((resData: PreviousAssignmentResponse) =>
                        new AssignmentsActions.LoadPreviousAssignmentSuccess(resData)
                    ),
                    catchError(error =>
                        of(new AssignmentsActions.LoadPreviousAssignmentFailed(error))
                    )
                );
            })
        )
    );
}